import { File, Paths } from 'expo-file-system';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';

import { routeGeometryToGpx, type RouteLineString } from '@sixam/shared/route-core';

function routeFileName(name: string) {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 64);
  return `${slug || '6am-route'}.gpx`;
}

function webGpxFile(geometry: RouteLineString, name: string) {
  const contents = routeGeometryToGpx(geometry, name);
  return new globalThis.File([contents], routeFileName(name), {
    type: 'application/gpx+xml',
  });
}

function downloadWebFile(file: globalThis.File) {
  const url = URL.createObjectURL(file);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = file.name;
  anchor.click();
  URL.revokeObjectURL(url);
}

function nativeGpxFile(geometry: RouteLineString, name: string) {
  const file = new File(Paths.cache, routeFileName(name));
  file.create({ overwrite: true });
  file.write(routeGeometryToGpx(geometry, name));
  return file;
}

export async function downloadRouteGpx(geometry: RouteLineString, name: string) {
  if (Platform.OS === 'web') {
    downloadWebFile(webGpxFile(geometry, name));
    return;
  }

  const file = nativeGpxFile(geometry, name);
  if (!(await Sharing.isAvailableAsync())) {
    throw new Error('File export is unavailable on this device.');
  }
  await Sharing.shareAsync(file.uri, {
    dialogTitle: 'Save route GPX',
    mimeType: 'application/gpx+xml',
    UTI: 'com.topografix.gpx',
  });
}

export async function shareRouteGpx(geometry: RouteLineString, name: string) {
  if (Platform.OS === 'web') {
    const file = webGpxFile(geometry, name);
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: name.trim() || '6am route' });
      return;
    }
    downloadWebFile(file);
    return;
  }

  const file = nativeGpxFile(geometry, name);
  if (!(await Sharing.isAvailableAsync())) {
    throw new Error('Sharing is unavailable on this device.');
  }
  await Sharing.shareAsync(file.uri, {
    dialogTitle: 'Share route GPX',
    mimeType: 'application/gpx+xml',
    UTI: 'com.topografix.gpx',
  });
}
